import { Api, Fixtuur } from '@fixtuur/backpack';
import { getIds } from '@fixtuur/backpack/src/render/particle/tree';
import * as Brenda from '@fixtuur/brenda';
import * as TE from 'fp-ts/TaskEither';
import { identity, pipe } from 'fp-ts/lib/function';
import * as Hooks from 'preact/hooks';
import * as SkuDataCollectionActions from '../store/actions/app';
import * as Basket from '../store/basket';
import { useDispatch, useSelector } from '../store/redux';
import * as Selectors from '../store/selectors';
import { useAsyncThrowError } from './useAsync';
import { useCachingAsyncCallback } from './useCachingAsyncCallback';
import { useFeatureFlag } from './useFeatureFlag';
import { isPublished } from './useParentApi/getPublishedStatus';

const EMPTY_BASKET: Api.Configuration.Basket.Basket = { products: [], price: 0, currency: 'GBP' };

export const useGetBasket = (
    furniture: Brenda.Renderer.Furniture.Furniture | undefined,
    currentProduct: Fixtuur.Product.Product | null,
) => {
    const removePricingEnabled = useFeatureFlag('remove-pricing-enabled');
    const { getBasketForParticles } = useCachingGetBasketforParticles();
    const dispatch = useDispatch();

    const serviceConfig = useSelector(Selectors.Core.serviceConfig);
    const tenant = useSelector(Selectors.Core.tenant);

    // in config, the basket call gets the currency for us, but this isn't the case in inspiration
    // so get currency via the store
    const currency = useSelector(Selectors.Core.currency);
    const options = useSelector(Selectors.Options.productOptions);
    const { result: basket } = useAsyncThrowError(async () => {
        if (!serviceConfig) {
            throw new Error('attempted to get basket before service config set');
        }
        if (!tenant) {
            throw new Error('attempted to get basket before tenant set');
        }
        if (removePricingEnabled) {
            return EMPTY_BASKET;
        } else if (furniture && options) {
            // Configuration
            const complexProduct = Fixtuur.Configuration.ComplexProduct.fromOptions(options);
            const particlesInFurniture = getIds(furniture.particleTree);
            const basketForParticles = await getBasketForParticles(
                particlesInFurniture,
                complexProduct,
            );

            const productsWithPublishedStatuses = await Promise.all(
                basketForParticles.products.map(async p => ({
                    ...p,
                    published: await isPublished(serviceConfig.manifest, p.productId, tenant),
                })),
            );

            return {
                ...basketForParticles,
                products: productsWithPublishedStatuses,
            };
        } else if (currentProduct) {
            // Inspiration
            return {
                products: [
                    {
                        productId: Fixtuur.Product.id(currentProduct),
                        quantity: 1,
                        unitPrice: Fixtuur.Product.price(currentProduct),
                        published: await isPublished(
                            serviceConfig.manifest,
                            Fixtuur.Product.id(currentProduct),
                            tenant,
                        ),
                    },
                ],
                price: Fixtuur.Product.price(currentProduct),
                currency: currency ?? 'GBP',
            };
        }
    }, [
        currentProduct,
        furniture?.particleTree,
        getBasketForParticles,
        removePricingEnabled,
        options,
    ]);

    Hooks.useEffect(() => {
        dispatch(Basket.update(basket));
        dispatch(SkuDataCollectionActions.skuViewAction());
    }, [basket]);
};

class GetBasketError extends Error {
    constructor(
        public source: unknown,
        public particles: string[],
        public options: Record<string, string | number> | null,
    ) {
        super('Could not get basket');
    }
}

function useCachingGetBasketforParticles() {
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);

    const cached = useCachingAsyncCallback(
        async (particles: any, complexProduct: any) => {
            if (!serviceConfig) {
                throw new Error('attempted to get basket before service config set');
            }

            return await pipe(
                Api.Configuration.Basket.get(serviceConfig, particles, complexProduct),
                TE.match(error => {
                    throw new GetBasketError(error, particles, complexProduct);
                }, identity),
            )();
        },
        [serviceConfig],
    );

    const stable = Hooks.useCallback(
        (
            furnitureParticles: Array<string>,
            complexProduct: Record<string, string | number> | null,
        ) => cached(furnitureParticles.slice().sort(), complexProduct),
        [cached],
    );

    return {
        getBasketForParticles: stable,
    };
}
